import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react'
import { bookmarkApi, type BookmarkFolder } from '../api/bookmarks'
import { useAuth } from './AuthContext'
import { useCorpus } from './CorpusContext'

interface BookmarkContextValue {
  folders: BookmarkFolder[]
  totalCount: number
  isBookmarked: (pmcid: string) => boolean
  addBookmark: (pmcid: string, title?: string | null, folderId?: string | null) => Promise<void>
  removeBookmark: (pmcid: string) => Promise<void>
  createFolder: (name: string) => Promise<BookmarkFolder>
  refresh: () => Promise<void>
}

const BookmarkContext = createContext<BookmarkContextValue | null>(null)

export function BookmarkProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth()
  const { activeCorpusId } = useCorpus()
  const [folders, setFolders] = useState<BookmarkFolder[]>([])
  const [ids, setIds] = useState<Set<string>>(new Set())

  const refresh = useCallback(async () => {
    if (!isAuthenticated) { setFolders([]); setIds(new Set()); return }
    try {
      const [folderList, pmcids] = await Promise.all([
        bookmarkApi.listFolders(),
        bookmarkApi.listIds(),
      ])
      setFolders(folderList)
      setIds(new Set(pmcids))
    } catch {
      // ignore — leave current state
    }
  }, [isAuthenticated])

  useEffect(() => { refresh() }, [refresh])

  const isBookmarked = useCallback((pmcid: string) => ids.has(pmcid), [ids])

  const addBookmark = useCallback(async (pmcid: string, title?: string | null, folderId?: string | null) => {
    await bookmarkApi.addBookmark(pmcid, title, folderId, activeCorpusId || null)
    setIds(prev => new Set(prev).add(pmcid))
    // refresh folder counts in the background
    bookmarkApi.listFolders().then(setFolders).catch(() => {})
  }, [activeCorpusId])

  const removeBookmark = useCallback(async (pmcid: string) => {
    await bookmarkApi.removeBookmark(pmcid)
    setIds(prev => { const n = new Set(prev); n.delete(pmcid); return n })
    bookmarkApi.listFolders().then(setFolders).catch(() => {})
  }, [])

  const createFolder = useCallback(async (name: string) => {
    const folder = await bookmarkApi.createFolder({ name })
    setFolders(prev => [...prev, folder])
    return folder
  }, [])

  return (
    <BookmarkContext.Provider value={{ folders, totalCount: ids.size, isBookmarked, addBookmark, removeBookmark, createFolder, refresh }}>
      {children}
    </BookmarkContext.Provider>
  )
}

export function useBookmarks() {
  const ctx = useContext(BookmarkContext)
  if (!ctx) throw new Error('useBookmarks must be used within a BookmarkProvider')
  return ctx
}
